import { useSyncExternalStore } from "react";
import {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
  isAxiosError,
} from "axios";
import { axiosInstance } from "../../../utils/axios";

// The developers' request log (test environment only): the panel's last
// requests to the backend, kept in this tab's memory, so a failing one can be
// told by its UTC time and replayed as cURL.

export type LoggedRequest = {
  id: number;
  // when the answer came, in ms
  at: number;
  method: string;
  url: string;
  // null: no answer at all (network)
  status: number | null;
  durationMs: number | null;
  // the backend's message of a failed request
  message?: string;
  body?: string;
};

const MAX_ENTRIES = 50;

let entries: LoggedRequest[] = [];
let nextId = 0;
let started = false;
const listeners = new Set<() => void>();
const startedAt = new WeakMap<object, number>();

const emit = () => listeners.forEach((listener) => listener());

const bodyOf = (data: unknown) => {
  if (data === undefined || data === null || data === "") return undefined;
  return typeof data === "string" ? data : JSON.stringify(data);
};

const messageOf = (error: AxiosError) => {
  const data = error.response?.data as
    | { metadata?: { message?: string } }
    | undefined;
  return data?.metadata?.message || error.message;
};

const record = (
  config: InternalAxiosRequestConfig | undefined,
  status: number | null,
  message?: string,
) => {
  if (!config) return;
  const start = startedAt.get(config);
  const entry: LoggedRequest = {
    id: (nextId += 1),
    at: Date.now(),
    method: (config.method ?? "get").toUpperCase(),
    url: axiosInstance.getUri(config),
    status,
    durationMs: start === undefined ? null : Date.now() - start,
    message,
    body: bodyOf(config.data),
  };
  entries = [entry, ...entries].slice(0, MAX_ENTRIES);
  emit();
};

// Starts listening, once. The listeners only look: every request and answer
// is handed on unchanged, so the shared interceptors (token refresh, error
// handling, which resolves failed requests instead of rejecting them) work
// exactly as before.
export const startRequestLog = () => {
  if (started) return;
  started = true;
  axiosInstance.interceptors.request.use((config) => {
    startedAt.set(config, Date.now());
    return config;
  });
  axiosInstance.interceptors.response.use(
    (value) => {
      // the shared interceptor hands failed requests on as values
      const answer = value as unknown;
      if (isAxiosError(answer)) {
        record(
          answer.config,
          answer.response?.status ?? null,
          messageOf(answer),
        );
      } else {
        const response = answer as AxiosResponse;
        record(response.config, response.status);
      }
      return value;
    },
    (error) => {
      if (isAxiosError(error)) {
        record(error.config, error.response?.status ?? null, messageOf(error));
      }
      return Promise.reject(error);
    },
  );
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
const snapshot = () => entries;

export const useRequestLog = () => useSyncExternalStore(subscribe, snapshot);

export const clearRequestLog = () => {
  entries = [];
  emit();
};

export const isFailed = (entry: LoggedRequest) =>
  entry.status === null || entry.status >= 400;

// The request as a cURL command; the token stays a placeholder ($TOKEN), to
// be filled from "My session".
export const toCurl = (entry: LoggedRequest) => {
  const quote = (value: string) => `'${value.replace(/'/g, "'\\''")}'`;
  return [
    `curl -X ${entry.method} ${quote(entry.url)}`,
    `-H 'Authorization: Bearer $TOKEN'`,
    ...(entry.body
      ? [`-H 'Content-Type: application/json'`, `--data ${quote(entry.body)}`]
      : []),
  ].join(" \\\n  ");
};
